import React from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@cobliteam/ui/components';
import queryString from 'query-string';

import { Location } from '@cobliteam/common-types';

import { ConditionCategory } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/conditions';
import type { TriggerEvent } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/events';

import { Analytics } from '@cobliteam/analytics';
import { getTime } from '@cobliteam/utils/date';

import { DatadogTrack } from '@cobliteam/datadog-rum';
import Icon from '@cobliteam/ui/icons';

const getTriggerEventIcon = (triggerEvent: TriggerEvent) => {
  const {
    meta: { conditionCategory },
  } = triggerEvent;

  switch (conditionCategory) {
    case ConditionCategory.GEOFENCE_IN:
    case ConditionCategory.GEOFENCE_OUT:
    case ConditionCategory.GEOFENCE_IN_OUT:
    case ConditionCategory.GEOFENCE_SPEED:
      return <Icon name='map-pinned' />;

    case ConditionCategory.IDLE_ENGINE_3_MINUTES:
    case ConditionCategory.IDLE_ENGINE_5_MINUTES:
    case ConditionCategory.IDLE_ENGINE_10_MINUTES:
    case ConditionCategory.IDLE_ENGINE_15_MINUTES:
    case ConditionCategory.IDLE_ENGINE_30_MINUTES:
      return <Icon name='clock' />;

    case ConditionCategory.DEVICE_CONNECTION_CONNECTED:
    case ConditionCategory.DEVICE_CONNECTION_DISCONNECTED:
    case ConditionCategory.DEVICE_CONNECTION_CONNECTED_DISCONNECTED:
      return <Icon name='unplug' />;

    case ConditionCategory.VEHICLE_SPEED_LIMIT_1_MINUTE:
    case ConditionCategory.VEHICLE_SPEED_LIMIT_5_MINUTES:
    case ConditionCategory.VEHICLE_SPEED_LIMIT_10_MINUTES:
      return <Icon name='zap' />;

    case ConditionCategory.ROAD_SPEED_LIMIT_1_MINUTE:
    case ConditionCategory.ROAD_SPEED_LIMIT_5_MINUTES:
    case ConditionCategory.ROAD_SPEED_LIMIT_10_MINUTES:
      return <Icon name='gauge' />;

    case ConditionCategory.DRIVER_IDENTIFICATION_WITHOUT_DRIVER:
      return <Icon name='square-user-round' />;

    case ConditionCategory.CHECKLIST_UNFILLED:
      return <Icon name='list-checks' />;

    case ConditionCategory.DRIVER_CAMERA_OBSTRUCTED_OR_UNOBSTRUCTED:
    case ConditionCategory.DRIVER_CAMERA_OBSTRUCTED:
    case ConditionCategory.DRIVER_CAMERA_UNOBSTRUCTED:
      return <Icon name='video-off' color='danger' />;

    default: {
      DatadogTrack.error(new Error('Trigger event icon not found'));
      return <Icon name='circle-x' />;
    }
  }
};

const getTriggerPrimaryButton = (triggerEvent: TriggerEvent, eventType: string) => {
  const { vehicleId, eventTime, id: eventId } = triggerEvent;

  if (!vehicleId) return;
  return (
    <Link
      to={{
        pathname: Location.TRACKING,
        search: `?${queryString.stringify({
          vehicle_id: vehicleId,
          date: getTime(new Date(eventTime), 'start'),
          event_type: eventType,
          event_id: eventId,
          event_time: getTime(new Date(eventTime), 'start'),
        })}`,
      }}
      target='_blank'
      rel='noopener noreferrer'
      className='ml-auto flex w-fit'
      onClick={() => Analytics.track('Notification Tracking History Clicked')}
    >
      <Button variant='outlined' color='default' size='sm' startIcon={<Icon name='route' />}>
        Ver trajeto
      </Button>
    </Link>
  );
};

const getTriggerEventInfo = (triggerEvent: TriggerEvent) => {
  const { meta } = triggerEvent;
  const icon = React.cloneElement(getTriggerEventIcon(triggerEvent) as React.ReactElement, {
    className: 'size-4 bg-gray-lightest rounded-full p-1 box-content overflow-visible',
  });

  switch (meta.conditionCategory) {
    // Geofence
    case ConditionCategory.GEOFENCE_IN:
      return {
        icon,
        rule: 'Gestão de locais',
        condition: 'Entrada no local',
        eventType: 'geofence',
        path: Location.FLEET_POLICY_GEOFENCE,
      };
    case ConditionCategory.GEOFENCE_OUT:
      return {
        icon,
        rule: 'Gestão de locais',
        condition: 'Saída do local',
        eventType: 'geofence',
        path: Location.FLEET_POLICY_GEOFENCE,
      };
    case ConditionCategory.GEOFENCE_IN_OUT:
      return {
        icon,
        rule: 'Gestão de locais',
        condition: 'Entrada ou saída do local',
        eventType: 'geofence',
        path: Location.FLEET_POLICY_GEOFENCE,
      };

    case ConditionCategory.GEOFENCE_SPEED:
      return {
        icon,
        rule: 'Gestão de locais',
        condition: 'Velocidade dentro do local',
        eventType: 'geofence',
        path: Location.FLEET_POLICY_GEOFENCE,
      };

    // Idle engine
    case ConditionCategory.IDLE_ENGINE_3_MINUTES:
      return {
        icon,
        rule: 'Motor ocioso',
        condition: 'A partir de 3 minutos',
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };
    case ConditionCategory.IDLE_ENGINE_5_MINUTES:
      return {
        icon,
        rule: 'Motor ocioso',
        condition: 'A partir de 5 minutos',
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };
    case ConditionCategory.IDLE_ENGINE_10_MINUTES:
      return {
        icon,
        rule: 'Motor ocioso',
        condition: 'A partir de 10 minutos',
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };
    case ConditionCategory.IDLE_ENGINE_15_MINUTES:
      return {
        icon,
        rule: 'Motor ocioso',
        condition: 'A partir de 15 minutos',
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };
    case ConditionCategory.IDLE_ENGINE_30_MINUTES:
      return {
        icon,
        rule: 'Motor ocioso',
        condition: 'A partir de 30 minutos',
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };

    // Device connection
    case ConditionCategory.DEVICE_CONNECTION_CONNECTED:
      return {
        icon,
        rule: 'Situação do dispositivo',
        condition: 'Conectado',
        eventType: 'incident',
        path: Location.FLEET_POLICY_DEVICE_CONNECTION,
      };
    case ConditionCategory.DEVICE_CONNECTION_DISCONNECTED:
      return {
        icon,
        rule: 'Situação do dispositivo',
        condition: 'Desconectado',
        eventType: 'incident',
        path: Location.FLEET_POLICY_DEVICE_CONNECTION,
      };
    case ConditionCategory.DEVICE_CONNECTION_CONNECTED_DISCONNECTED:
      return {
        icon,
        rule: 'Situação do dispositivo',
        condition: 'Conectado e desconectado',
        eventType: 'incident',
        path: Location.FLEET_POLICY_DEVICE_CONNECTION,
      };

    // Vehicle speed limit
    case ConditionCategory.VEHICLE_SPEED_LIMIT_1_MINUTE:
      return {
        icon,
        rule: 'Excesso de velocidade do veículo',
        condition: 'A partir de 1 minuto dirigido acima do limite',
        eventType: 'speed',
        path: Location.FLEET_POLICY_VEHICLE_SPEED_LIMIT,
      };
    case ConditionCategory.VEHICLE_SPEED_LIMIT_5_MINUTES:
      return {
        icon,
        rule: 'Excesso de velocidade do veículo',
        condition: 'A partir de 5 minutos dirigidos acima do limite',
        eventType: 'speed',
        path: Location.FLEET_POLICY_VEHICLE_SPEED_LIMIT,
      };
    case ConditionCategory.VEHICLE_SPEED_LIMIT_10_MINUTES:
      return {
        icon,
        rule: 'Excesso de velocidade do veículo',
        condition: 'A partir de 10 minutos dirigidos acima do limite',
        eventType: 'speed',
        path: Location.FLEET_POLICY_VEHICLE_SPEED_LIMIT,
      };

    // Road speed limit
    case ConditionCategory.ROAD_SPEED_LIMIT_1_MINUTE:
      return {
        icon,
        rule: 'Excesso de velocidade da via',
        condition: 'A partir de 1 minuto dirigido acima do limite',
        eventType: 'speed',
        path: Location.FLEET_POLICY_ROAD_SPEED_LIMIT,
      };
    case ConditionCategory.ROAD_SPEED_LIMIT_5_MINUTES:
      return {
        icon,
        rule: 'Excesso de velocidade da via',
        condition: 'A partir de 5 minutos dirigido acima do limite',
        eventType: 'speed',
        path: Location.FLEET_POLICY_ROAD_SPEED_LIMIT,
      };
    case ConditionCategory.ROAD_SPEED_LIMIT_10_MINUTES:
      return {
        icon,
        rule: 'Excesso de velocidade da via',
        condition: 'A partir de 10 minutos dirigido acima do limite',
        eventType: 'speed',
        path: Location.FLEET_POLICY_ROAD_SPEED_LIMIT,
      };

    // Checklist
    case ConditionCategory.CHECKLIST_UNFILLED:
      return {
        icon,
        rule: 'Checklist',
        condition: 'Checklist não preenchida',
        eventType: 'checklist',
        path: Location.FLEET_POLICY_CHECKLIST,
      };

    // Driver identification
    case ConditionCategory.DRIVER_IDENTIFICATION_WITHOUT_DRIVER:
      return {
        icon,
        rule: 'Identificação de motorista',
        condition: 'Motorista não identificado',
        eventType: 'driver-identification',
        path: Location.FLEET_POLICY_DRIVER_IDENTIFICATION,
      };

    // Cameras Health
    case ConditionCategory.DRIVER_CAMERA_OBSTRUCTED_OR_UNOBSTRUCTED:
      return {
        icon,
        rule: 'Saúde das câmeras',
        condition: 'Câmera obstruída ou desobstruída',
        eventType: 'cameras-health',
        path: Location.FLEET_POLICY_CAMERAS_HEALTH,
      };

    case ConditionCategory.DRIVER_CAMERA_OBSTRUCTED:
      return {
        icon,
        rule: 'Saúde das câmeras',
        condition: 'Câmera obstruída',
        eventType: 'cameras-health',
        path: Location.FLEET_POLICY_CAMERAS_HEALTH,
      };

    case ConditionCategory.DRIVER_CAMERA_UNOBSTRUCTED:
      return {
        icon,
        rule: 'Saúde das câmeras',
        condition: 'Câmera desobstruída',
        eventType: 'cameras-health',
        path: Location.FLEET_POLICY_CAMERAS_HEALTH,
      };

    default:
      return {
        rule: '',
        condition: '',
        icon: null,
        eventType: '',
        path: '',
      };
  }
};

export { getTriggerEventInfo, getTriggerPrimaryButton };
